/** Кусочки UI «Аналитики ОПТ»: ошибка API, ячейка денег с пометкой «часть без цены» и Δ, свежесть,
 *  «что не вошло», значок покрытия ценой, KPI отгрузок с Δ, портфель заказов. */
import { AlertTriangle, ChevronRight } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import type { Agg, OutsideAgg, PerKind, WholesaleState, WholesaleSummary } from "@/api/wholesale";
import { useAuth } from "@/auth/useAuth";
import { cn } from "@/lib/utils";
import { TONE_CLASS } from "../ozon-analytics/calc";
import { fmtBuiltMsk, fmtDate, fmtWhen, martStaleWarning, monthLabel, rub, timeAgo } from "../ozon-analytics/format";
import { KpiGrid } from "../ozon-analytics/pnl";
import { Unreliable } from "../ozon-analytics/shared";
import { apiErrorText, tdNum } from "../ozon-analytics/ui";
import {
  coverage,
  coverageChipText,
  coverageLineText,
  excludedLines,
  flags,
  freshnessText,
  marginCell,
  outsideNote,
  qty,
  revenueCell,
  type CoverageLine,
  type MoneyCell,
  type OpenKind,
} from "./calc";
import { shippedKpis, type DeltaPart } from "./kpi";

export const AMBER =
  "rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300";
export const RED =
  "rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400";

export function ErrorBox({ error, what }: { error: unknown; what: string }): React.ReactElement {
  return (
    <div role="alert" className={RED} data-testid="wholesale-error">
      Не удалось получить {what}. {apiErrorText(error, "опта")}
    </div>
  );
}

/** Ячейка денег: «—» с причиной в подсказке; частичная сумма — с жёлтой звёздочкой; `delta` —
 *  вторая строка мелко (Δ к периоду сравнения, как в дереве «Аналитики МП»). */
export function MoneyTd({
  cell,
  className,
  testId,
  delta,
  deltaWarning = null,
}: {
  cell: MoneyCell;
  className?: string;
  testId?: string;
  delta?: DeltaPart | null;
  /** Δ ненадёжна (сравнение вне данных) — ⚠ у Δ, как в KPI */
  deltaWarning?: string | null;
}): React.ReactElement {
  const title = [cell.title, delta?.title].filter(Boolean).join("\n") || undefined;
  return (
    <td className={cn(tdNum, className)} title={title} data-testid={testId}>
      <div className={cn(delta !== undefined && "leading-4")}>
        {cell.text}
        {cell.partial && (
          <span className="ml-0.5 text-amber-600 dark:text-amber-400" aria-label="часть без цены" data-testid="partial">
            *
          </span>
        )}
      </div>
      {delta && <DeltaLine d={delta} warning={deltaWarning} />}
    </td>
  );
}

/** Вторая строка ячейки — Δ мелко, цветом по смыслу. */
function DeltaLine({ d, warning }: { d: DeltaPart; warning: string | null }): React.ReactElement {
  return (
    <div className={cn("text-[11px] font-normal leading-3", TONE_CLASS[d.tone])} data-testid="delta">
      {d.text}
      <Unreliable warning={warning} />
    </div>
  );
}

function usePurchasePricesAccess(): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return user.is_admin || user.permissions.includes("purchase_prices");
}

/** Строка свежести + предупреждение «витрина не пересобиралась» (как в «Аналитике МП»). */
export function Freshness({ state }: { state: WholesaleState }): React.ReactElement {
  const stale = martStaleWarning(state.built_at);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground" data-testid="freshness">
        <span>
          Витрина собрана {fmtBuiltMsk(state.built_at)} ({timeAgo(state.built_at)})
        </span>
        <span>·</span>
        <span>{freshnessText(state)}</span>
        <span>·</span>
        <span>
          загрузка продаж {fmtWhen(state.sales_loaded_at)}, снимок заказов {fmtWhen(state.orders_snapshot_at)}
        </span>
      </div>
      {stale && (
        <div role="status" className={AMBER} data-testid="stale">
          {stale}. Цифры — на момент той сборки.
        </div>
      )}
    </div>
  );
}

/** «Что не вошло в витрину» — из `wholesale_state`; новая причина и неклассифицированное
 *  состояние заказа — красным, раскрыто. */
export function Excluded({ state }: { state: WholesaleState }): React.ReactElement {
  const lines = excludedLines(state);
  const alert = lines.some((l) => l.alert);
  const unres = state.shipped_month_unresolved;
  return (
    <details className={cn("rounded-md border px-3 py-2 text-sm", alert && "border-red-300 dark:border-red-900")} open={alert} data-testid="excluded">
      <summary className="cursor-pointer select-none">
        Что не вошло в витрину {lines.length === 0 ? "— ничего" : `— ${lines.length} ${lines.length === 1 ? "причина" : "причин(ы)"}`}
        {alert && <span className="ml-2 font-medium text-red-700 dark:text-red-400">есть неклассифицированное</span>}
      </summary>
      <ul className="mt-2 space-y-1">
        {lines.map((l) => (
          <li
            key={`${l.where}:${l.reason}`}
            data-testid="excluded-line"
            data-alert={l.alert ? "1" : "0"}
            className={cn(l.alert && "text-red-700 dark:text-red-400")}
          >
            {l.where === "shipped" ? "Отгрузки" : "Остатки заказов"} — {l.label}: {l.text}
          </li>
        ))}
        {unres.rows > 0 && (
          <li data-testid="excluded-unresolved" className="text-amber-800 dark:text-amber-300">
            В витрине, но месяцем корректировки (исходная накладная не найдена): {unres.rows} строк
            {unres.revenue_gross !== null && `, ${rub(unres.revenue_gross)}`}
          </li>
        )}
      </ul>
    </details>
  );
}

/** Пояснения покрытия (не пробелы): отгрузки за 0 ₽ (`zero_amount`, v1.4) — штуки есть, выручка 0,
 *  маржа — только расход. Нейтрально, рядом со значком. */
function CoverageNotes({ notes }: { notes: CoverageLine[] }): React.ReactElement | null {
  if (notes.length === 0) return null;
  return (
    <ul className="text-xs text-muted-foreground" data-testid="coverage-notes">
      {notes.map((l) => (
        <li key={`${l.kind}:${l.status}`} data-testid="coverage-note">
          {coverageLineText(l)}
        </li>
      ))}
    </ul>
  );
}

/** Покрытие закупочной ценой и суммой заказа по области экрана (отгрузки и к отгрузке периода + все
 *  просроченные) — одной строкой-значком; есть пробелы — по клику разбивка по видам и статусам
 *  и кнопки списков «без цены» / «без ставки склада». Под значком — пояснения (`CoverageNotes`). */
export function CoverageChip({
  scope,
  onShowUnpriced,
  onShowNoRate,
}: {
  scope: PerKind;
  onShowUnpriced: () => void;
  onShowNoRate: () => void;
}): React.ReactElement | null {
  const c = coverage(scope);
  const canPrices = usePurchasePricesAccess();
  if (c.tone === "empty") return null;
  if (c.tone === "ok") {
    return (
      <div className="space-y-1">
        <div
          className="inline-flex rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-400"
          data-testid="coverage"
          data-tone="ok"
        >
          {coverageChipText(c)}
        </div>
        <CoverageNotes notes={c.notes} />
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <details className="group text-xs" data-testid="coverage" data-tone="warn">
        <summary className="inline-flex cursor-pointer select-none items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
          <AlertTriangle className="size-3.5" aria-hidden />
          {coverageChipText(c)}
          <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden />
        </summary>
        <div className={cn(AMBER, "mt-1 text-xs")}>
          <ul className="list-disc pl-5">
            {c.lines.map((l) => (
              <li key={`${l.kind}:${l.status}`} data-testid="coverage-line">
                {coverageLineText(l)}
              </li>
            ))}
          </ul>
          {(c.hasUnpriced || c.hasNoRate) && (
            <div className="mt-1 flex flex-wrap gap-3">
              {c.hasUnpriced && (
                <button type="button" className="font-medium underline" onClick={onShowUnpriced} data-testid="show-unpriced">
                  Товары без себестоимости
                </button>
              )}
              {c.hasNoRate && (
                <button type="button" className="font-medium underline" onClick={onShowNoRate} data-testid="show-norate">
                  Товары без ставки склада
                </button>
              )}
              {canPrices && (
                <Link to="/purchase-prices" className="font-medium underline" data-testid="prices-link">
                  Закупочные цены
                </Link>
              )}
            </div>
          )}
        </div>
      </details>
      <CoverageNotes notes={c.notes} />
    </div>
  );
}

export function Flags({ totals }: { totals: WholesaleSummary["totals"] }): React.ReactElement | null {
  const f = flags(totals);
  if (f.length === 0) return null;
  return (
    <ul className={cn(AMBER, "list-disc space-y-0.5 pl-8")} data-testid="flags">
      {f.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

/** KPI отгрузок периода с Δ к периоду сравнения — та же сетка, что в «Аналитике МП». */
export function ShippedKpis({
  shipped,
  cmp,
  warning,
  compact = false,
}: {
  shipped: Agg | null;
  /** undefined — сравнения нет; null — в периоде сравнения отгрузок не было */
  cmp: Agg | null | undefined;
  /** Δ ненадёжна (сравнение вне данных) — ⚠ у Δ */
  warning: string | null;
  compact?: boolean;
}): React.ReactElement {
  return (
    <div data-testid="kpi-shipped">
      <KpiGrid kpis={shippedKpis(shipped, cmp)} warning={warning} compact={compact} />
    </div>
  );
}

const OPEN_TONE: Record<OpenKind, string> = {
  planned: "text-sky-700 dark:text-sky-400",
  overdue: "text-red-700 dark:text-red-400",
};

type OpenAgg = Agg & { month_min?: string; month_max?: string };

function spanText(a: OpenAgg): string | null {
  if (!a.month_min || !a.month_max) return null;
  return a.month_min === a.month_max
    ? `отгрузка — ${monthLabel(a.month_min)}`
    : `отгрузка — ${monthLabel(a.month_min)} … ${monthLabel(a.month_max)}`;
}

/** Справка под «К отгрузке»: заказы с датой отгрузки вне периода — ни во что не входят. */
function OutsideNote({ side, a }: { side: "after" | "before"; a: OutsideAgg }): React.ReactElement {
  const n = outsideNote(side, a);
  return (
    <div className="mt-0.5 text-[11px] text-muted-foreground" title={n.title} data-testid={`portfolio-planned-${side}`}>
      {n.text}
    </div>
  );
}

function OpenCard({
  kind,
  label,
  a,
  onOpen,
  children,
}: {
  kind: OpenKind;
  label: string;
  a: OpenAgg | null;
  onOpen?: () => void;
  children?: React.ReactNode;
}): React.ReactElement {
  const rev = revenueCell(a);
  const m = marginCell(a);
  const span = a ? spanText(a) : null;
  return (
    <div className="rounded-md border bg-card p-3" data-testid={`portfolio-${kind}`}>
      <div className={cn("text-xs", OPEN_TONE[kind])} data-testid={`portfolio-label-${kind}`}>
        {label}
      </div>
      {a ? (
        <>
          <div className="mt-1 text-xl font-semibold tabular-nums" title={rev.title ?? undefined}>
            {rev.text}
            {rev.partial && <span className="ml-0.5 text-base text-amber-600 dark:text-amber-400" data-testid="partial">*</span>}
          </div>
          <div className="mt-1 text-xs tabular-nums" title={m.title ?? undefined} data-testid="portfolio-margin">
            плановая маржа {m.text}
            {m.partial && <span className="text-amber-600 dark:text-amber-400" data-testid="partial">*</span>} · {m.pct}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {qty(a.quantity)} шт{span ? ` · ${span}` : ""}
            {onOpen && (
              <>
                {" · "}
                <button type="button" className="underline hover:text-foreground" onClick={onOpen} data-testid={`portfolio-open-${kind}`}>
                  строки
                </button>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="mt-1 text-xl font-semibold text-muted-foreground">нет</div>
      )}
      {children}
    </div>
  );
}

/** Портфель заказов на дату снимка (решение владельца 23.10.2026): «К отгрузке» — заказы с датой
 *  отгрузки в периоде, «Просрочено» — ВСЕ просроченные на дату снимка (это прошлое, не должно
 *  пропадать). Выручка и плановая маржа («нет цены — не ноль»), без Δ. Под «К отгрузке» — справка
 *  о заказах с отгрузкой позже (раньше) периода (`after` / `before`; ни во что не входит). */
export function Portfolio({
  planned,
  overdue,
  asOf,
  onOpen,
  periodBound = false,
  after = null,
  before = null,
}: {
  planned: OpenAgg | null;
  overdue: OpenAgg | null;
  asOf: string;
  onOpen?: (kind: OpenKind) => void;
  /** иерархия дерева с «Месяцем»: и просроченные — только с отгрузкой в месяцах периода */
  periodBound?: boolean;
  /** к отгрузке позже конца периода (справка) */
  after?: OutsideAgg | null;
  /** к отгрузке раньше начала периода (справка; бывает, если период в будущем) */
  before?: OutsideAgg | null;
}): React.ReactElement {
  const date = fmtDate(asOf);
  return (
    <section className="space-y-1" data-testid="portfolio">
      <h3 className="text-xs text-muted-foreground">
        Портфель заказов на {date} —{" "}
        {periodBound
          ? "к отгрузке и просрочено с отгрузкой в месяцах периода (разрез «Месяц»), без сравнения"
          : "к отгрузке — с отгрузкой в периоде, просрочено — все на дату снимка, без сравнения"}
      </h3>
      <div className="grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
        <OpenCard
          kind="planned"
          label="К отгрузке — в периоде"
          a={planned}
          onOpen={onOpen ? () => onOpen("planned") : undefined}
        >
          {before && <OutsideNote side="before" a={before} />}
          {after && <OutsideNote side="after" a={after} />}
        </OpenCard>
        <OpenCard
          kind="overdue"
          label={periodBound ? "Просрочено — с отгрузкой в периоде" : `Просрочено — все просроченные на ${date}`}
          a={overdue}
          onOpen={onOpen ? () => onOpen("overdue") : undefined}
        />
      </div>
    </section>
  );
}
